import { AccountUserType } from '__generated__/atlas-types'

const messages = {
  anyTime: 'Any time',
  last30Days: 'Last 30 days',
  last60Days: 'Last 60 days',
  last90Days: 'Last 90 days',
  last180Days: 'Last 180 days',
  neverLoggedIn: 'Never logged in',
  account: 'Account',
  accountHierarchySettings: 'Account Hierarchy Settings',
  accountSeats: (amount: number) => `${amount} active users`,
  activate: 'Activate',
  activateUser: 'Activate User',
  activateUserContent: 'Would you like to reactivate',
  activateUserQuestion: 'Reactivate User?',
  activateUsers: 'Activate Users',
  activateUsersContent: (userAmount: number) =>
    `Would you like to reactivate ${userAmount} Users?`,
  activateUsersMightExceedLimit: (userAmount: number) =>
    `Activating ${userAmount} users might put you over your organization seat limit and incur an additional cost.`,
  activateUsersQuestion: (userAmount: number) =>
    `Reactivate ${userAmount} Users?`,
  activating: 'Activating',
  activeUsers: 'Active Users',
  add: 'Add',
  addCCEmail:
    "+ Add additional email recipients for this user's order notifications",
  addNewUser: 'Add New User',
  addOrderEmails: 'Add Order Emails',
  addOrderEmailsSeparated: 'Add Order Emails (comma separated)',
  addUser: 'Add User',
  addUserExceedsLimit:
    'Adding this user will put you over your organization seat limit and incur an additional cost.',
  anotherBrandAdminMustChangeYourUserType:
    'Another Brand Admin on your account must change your user type',
  areYouSureYouWantToResendTheInviteEmail:
    'Are you sure you want to resend the invite email?',
  billingContacts: 'Billing Contacts',
  bulkUpdateOrderEmailError: 'Failed to update order emails on selected users.',
  bulkUpdateOrderEmailSuccess: (amount: number) =>
    `Successfully updated order emails on ${amount} users.`,
  bulkUpdateUserTypeConfirmation: (type: string) =>
    `Are you sure you want to update all selected users to the ${type} type?`,
  cancel: 'Cancel',
  ccEmails: 'Additional Order Notification Recipients (comma separated)',
  ccEmailsPlaceholder: 'Enter Emails',
  changeUserType: 'Change User Type',
  clearFilters: 'Clear filters',
  clearSearch: 'Clear search',
  clickHere: 'click here',
  confirm: 'Confirm',
  continue: 'Continue',
  copyPermissions: 'Copy Permissions',
  dataPermissions: 'Data Permissions',
  disable: 'Disable',
  disabledUsersError: 'You cannot copy permissions to disabled users.',
  disableSingleUserContent:
    'will not remove them from existing orders or connections, but they will no longer be assigned as the sales rep on new orders.',
  disableUser: 'Disable User',
  disableUserQuestion: 'Disable User?',
  disableUsers: 'Disable Users',
  disableUsersQuestion: (userAmount: number) => `Disable ${userAmount} Users?`,
  disabling: 'Disabling',
  duplicateSalesRepCode: (userEmail: string, disabled: boolean) =>
    `This sales rep code is already being used by ${userEmail}${disabled ? ' (deactivated)' : ''}. This may cause issues if you are integrated for orders.`,
  edit: 'Edit',
  editPermissions: 'Edit Permissions',
  editUser: 'Edit User',
  editUserDetails: 'Edit User Details',
  emailAlreadyExists:
    'This email address is already associated with another active user.',
  emailAlreadyExistsInactive:
    'This email address is already associated with a disabled user.',
  error: 'Error',
  errorUserTypeIsRequired: 'User Type is required.',
  errorWhileCreatingUser: 'There was an error while creating the user.',
  errorWhileUpdatingStatus: 'There was an error while updating status.',
  errorWhileUpdatingUser: 'There was an error while updating the user.',
  export: 'Export',
  here: 'here',
  ifReassignmentNeeded:
    'If needed, you can reassign their orders or connections at any time using our reassignment tools.',
  invalidEmail: 'Please provide a valid email address',
  inviteEmailSentError: 'Failed to send invite email.',
  inviteEmailSentSuccess: 'Invite email sent successfully.',
  lastAdminUserCannotBeChanged: 'The last admin user cannot be changed.',
  lastLogin: 'Last Login',
  loadingUsers: 'Loading users...',
  managePermissions: 'Manage permissions',
  manageRegions: 'Manage Regions',
  maxTargetUsersError: (maxTargetUsers: number) =>
    `You can only copy permissions to up to ${maxTargetUsers} users at a time.`,
  never: 'Never',
  newUserAdded: 'New user added!',
  noneAdded: 'None added',
  noFilterSelected: 'No Filter Selected',
  noUsersForFilters: 'No users found for the selected filters',
  noUsersForSearch: 'No users found for the search criteria',
  noUsersFound: 'No users found',
  noUsersRows:
    'Try updating your search or clearing your filters to see more results.',
  notAvailable: 'Not available',
  optionalSteps:
    'These steps are optional and can be done now, later, or not at all.',
  orderListPage: 'Order List Page',
  organization: 'Organization',
  organizationSeats: (limit: number | string) => `of ${limit} seats used`,
  permissionSettingsLink: 'permission settings',
  plusNContacts: (amount: number) => `+${amount} more`,
  reactivateUser: 'Reactivate User',
  reassignConnections: 'Reassign Connections',
  regions: 'Regions',
  resendInvite: 'Resend Invite',
  requiredEmail: 'Email address is required.',
  requiredSalesRep:
    'Sales Rep Code cannot be added without a Sales Rep Display Name.',
  salesRepCode: 'Sales Rep Code',
  salesRepCodePlaceholder: 'Enter Sales Rep Code',
  salesRepDisplayName: 'Sales Rep Display Name',
  salesRepDisplayNamePlaceholder: 'Enter Sales Rep Display Name',
  salesRepName: 'Sales Rep Name',
  saveChanges: 'Save Changes',
  searchUsers: 'Search users',
  seatsLimitExceeded:
    'Adding this user will put you over your organization seat limit and incur an additional cost.',
  setUpPermissionNow: 'Set up their permissions now',
  thisAccount: 'This Account',
  thisUserHasNoConnections: 'This user has no connections to reassign',
  toReassignConnections: 'To reassign their connections,',
  toReassignOrders: 'To reassign their orders, visit the',
  toSeeTheAdminsOnYourAccount: 'to see the admins on your account.',
  unableToActivateTheUser: 'Unable to activate the user.',
  userActivated: 'User Activated!',
  userActivatedSubtitle:
    'The user has been successfully activated, you can now edit their details.',
  userDeactivated: 'User Deactivated!',
  userDeactivatedSuccessfully: 'has been successfully deactivated',
  userEmail: 'User Email',
  userEmailPlaceholder: 'Enter user email',
  users: 'Users',
  usersActivated: 'Users Activated!',
  usersActivatedSuccessfully: 'users activated successfully.',
  usersDeactivated: 'Users Deactivated!',
  usersDeactivatedSuccessfully: 'users deactivated successfully.',
  userLimit: 'User Limit',
  usersSelected: 'Users selected',
  userStatus: 'User Status',
  userSuccessfullyUpdated: 'successfully updated.',
  userType: 'User Type',
  userTypeUpdatedError: 'Failed to update type on selected users.',
  userTypeUpdatedSuccess: (amount: number, type: string) =>
    `${amount} users updated to ${type}.`,
  userUpdated: 'User Updated!',
  viewPermissions: 'View Permissions',
  willPutYourOrganization:
    'will put your organization over its seat limit and may incur an additional cost.',
  wouldYouLikeToSetUp: 'Would you like to review permissions for',
  wouldYouLikeToSetUpUser: (userEmail: string) =>
    `${userEmail} has been added to your account. They will receive an invite email shortly.`,
  youCanActivateIt: 'You can reactivate the user by clicking',
  youCanEditUserDetails: 'You can edit their details by clicking',
  youCannotChangeAllAdminUsersType:
    'You cannot update the user type for all Admins. There needs to be at least one active Admin on the account.',
  youCannotDisableAllAdminUsersType:
    'You cannot disable all Admins. There must be at least one active Admin on this account.',
  youMustBeABrandAdminToAccessUserManagement:
    'You must be a Brand Admin to access User Management. View your',
  yourUserWasNotDisabled:
    'All selected users were disabled except your own. Another Brand Admin must disable you.',
  yourUserWasNotUpdated:
    'Your user was not updated. Another Brand Admin must change your user type.',
} as const

export const UserTypeLabels = {
  [AccountUserType.ADMIN]: 'Admin',
  [AccountUserType.INTERNAL]: 'Internal',
  [AccountUserType.USER]: 'User',
  [AccountUserType.NO_ACCESS]: 'No Access',
  [AccountUserType.UNSPECIFIED]: '',
}

export default messages
